import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import {
  ApiError,
  api,
  fmtAmount as fmt,
  formatPercent,
  rateFromPercent,
  rateToPercent,
  unwrap,
} from "../api";
import {
  Button,
  ChevronButton,
  DeleteButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  PercentInput,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { useResource } from "../useResource";
import { RecordPaymentForm } from "./shared";

/** Structural, sensitive access — same rung as the client-portal counterpart. */
export function canManagePortalAccess(role: string): boolean {
  return role === "manager" || role === "admin" || role === "owner";
}

/** What the form collects. Blank optional fields go up as null, which clears them. */
interface ContractorBody {
  name: string;
  kind: "individual" | "company";
  email?: string | null | undefined;
  phone?: string | null | undefined;
  taxId?: string | null | undefined;
  defaultRate?: string | null | undefined;
  rateUnit?: "hour" | "day" | "fixed" | null | undefined;
  paymentTermsDays?: number | undefined;
  defaultRetainagePct?: string | undefined;
  notes?: string | null | undefined;
}

type Contractor = Awaited<ReturnType<typeof listContractors>>["contractors"][number];

async function listContractors(slug: string) {
  return unwrap(await api.api.c[":slug"].contractors.$get({ param: { slug } }));
}

async function costSummaryByContractor(slug: string) {
  return unwrap(
    await api.api.c[":slug"].payables["cost-summary"]["by-contractor"].$get({ param: { slug } }),
  );
}

async function listContractorBills(slug: string, contractorId: string) {
  return unwrap(
    await api.api.c[":slug"].payables.bills.$get({ param: { slug }, query: { contractorId } }),
  );
}

export function Contractors({
  slug,
  role,
  onOpenContractor,
}: {
  slug: string;
  role: string;
  onOpenContractor: (id: string) => void;
}) {
  // `null` is "adding", a row is "editing that one", and undefined is closed —
  // one piece of state, so the form cannot be open in two modes at once.
  const [form, setForm] = useState<Contractor | null | undefined>(undefined);
  const del = useDeleteRecord(slug);
  const qc = useQueryClient();

  // Which contractor's outstanding bills are open for payment — one at a
  // time, and only that contractor's own bills ever appear underneath.
  const [payingId, setPayingId] = useState<string | null>(null);
  const [settlingBillId, setSettlingBillId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  // Which contractors have their portal-access panel open — a set, so more
  // than one can be reviewed side by side, same as the client-portal panel.
  const [portalOpen, setPortalOpen] = useState<Set<string>>(new Set());
  function togglePortal(id: string) {
    setPortalOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const r = useResource({
    key: ["contractors", slug],
    list: () => listContractors(slug),
    create: async (body: ContractorBody) =>
      unwrap(await api.api.c[":slug"].contractors.$post({ param: { slug }, json: body })),
    update: async (id: string, body: ContractorBody) =>
      unwrap(
        await api.api.c[":slug"].contractors[":id"].$patch({ param: { slug, id }, json: body }),
      ),
    // Commitments and bills show the contractor's name and terms.
    alsoInvalidate: [["commitments", slug]],
  });

  const summary = useQuery({
    queryKey: ["cost-summary-contractors", slug],
    queryFn: () => costSummaryByContractor(slug),
  });
  const summaryById = new Map((summary.data?.contractors ?? []).map((c) => [c.contractorId, c]));

  const payingBills = useQuery({
    queryKey: ["bills", slug, "contractor", payingId],
    enabled: !!payingId,
    queryFn: () => listContractorBills(slug, payingId!),
  });
  const owed = (payingBills.data?.bills ?? []).filter((b) => b.outstanding !== "0.00");

  const pay = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onMutate: () => setPayError(null),
    onSuccess: () => {
      setSettlingBillId(null);
      qc.invalidateQueries({ queryKey: ["bills", slug] });
      qc.invalidateQueries({ queryKey: ["cost-summary-contractors", slug] });
      qc.invalidateQueries({ queryKey: ["cost-summary", slug] });
    },
    onError: (e) =>
      setPayError(e instanceof ApiError ? e.message : "Something went wrong. Try again."),
  });

  const contractorsTable = useTable(r.query.data?.contractors ?? [], {
    filter: (c, q) => `${c.name} ${c.kind}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      name: (c) => c.name.toLowerCase(),
      kind: (c) => c.kind,
      retainage: (c) => Number(c.defaultRetainagePct),
      terms: (c) => c.paymentTermsDays,
      committed: (c) => Number(summaryById.get(c.id)?.committed ?? 0),
      billed: (c) => Number(summaryById.get(c.id)?.billed ?? 0),
      outstanding: (c) => Number(summaryById.get(c.id)?.outstanding ?? 0),
    },
  });

  function open(next: Contractor | null) {
    r.clearErrors();
    setForm(next);
  }

  async function submit(body: ContractorBody) {
    try {
      if (form) await r.update({ id: form.id, body });
      else await r.create(body);
      setForm(undefined);
    } catch {
      /* surfaced through r.formError */
    }
  }

  function togglePaying(id: string) {
    setSettlingBillId(null);
    setPayError(null);
    setPayingId(payingId === id ? null : id);
  }

  return (
    <Panel
      title="Contractors"
      action={
        <Button onClick={() => (form === undefined ? open(null) : setForm(undefined))}>
          {form === undefined ? "New contractor" : "Cancel"}
        </Button>
      }
    >
      {form !== undefined && (
        <ContractorForm
          // Keyed by row, so opening a second contractor's form doesn't inherit
          // the first one's half-typed values.
          key={form?.id ?? "new"}
          initial={form}
          busy={r.saving}
          formError={r.formError}
          fieldErrors={r.fieldErrors}
          onSubmit={submit}
        />
      )}

      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      {r.query.isPending ? (
        <Loading />
      ) : r.query.isError ? (
        <ErrorNote>{(r.query.error as Error).message}</ErrorNote>
      ) : r.query.data.contractors.length === 0 ? (
        <Empty title="No contractors yet">Add one to start tracking committed cost.</Empty>
      ) : (
        <>
          <TableSearch
            value={contractorsTable.query}
            onChange={contractorsTable.setQuery}
            placeholder="Filter contractors…"
          />
          <Table
            head={
              <>
                <Th
                  sortKey="name"
                  active={contractorsTable.sortKey === "name"}
                  dir={contractorsTable.dir}
                  onSort={contractorsTable.toggleSort}
                >
                  Name
                </Th>
                <Th
                  sortKey="kind"
                  active={contractorsTable.sortKey === "kind"}
                  dir={contractorsTable.dir}
                  onSort={contractorsTable.toggleSort}
                >
                  Type
                </Th>
                <Th
                  right
                  sortKey="retainage"
                  active={contractorsTable.sortKey === "retainage"}
                  dir={contractorsTable.dir}
                  onSort={contractorsTable.toggleSort}
                >
                  Retainage
                </Th>
                <Th
                  right
                  sortKey="terms"
                  active={contractorsTable.sortKey === "terms"}
                  dir={contractorsTable.dir}
                  onSort={contractorsTable.toggleSort}
                >
                  Terms
                </Th>
                <Th
                  right
                  sortKey="committed"
                  active={contractorsTable.sortKey === "committed"}
                  dir={contractorsTable.dir}
                  onSort={contractorsTable.toggleSort}
                >
                  Committed
                </Th>
                <Th
                  right
                  sortKey="billed"
                  active={contractorsTable.sortKey === "billed"}
                  dir={contractorsTable.dir}
                  onSort={contractorsTable.toggleSort}
                >
                  Billed
                </Th>
                <Th
                  right
                  sortKey="outstanding"
                  active={contractorsTable.sortKey === "outstanding"}
                  dir={contractorsTable.dir}
                  onSort={contractorsTable.toggleSort}
                >
                  Outstanding
                </Th>
                <Th />
              </>
            }
          >
            {contractorsTable.rows.map((c) => {
              const cs = summaryById.get(c.id);
              const owesThem = !!cs && cs.outstanding !== "0.00";
              const isPortalOpen = portalOpen.has(c.id);
              return (
                <Fragment key={c.id}>
                  <tr className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100">
                    <Td>{c.name}</Td>
                    <Td muted>{c.kind}</Td>
                    <Td right mono muted>
                      {formatPercent(c.defaultRetainagePct)}
                    </Td>
                    <Td right mono muted>
                      {c.paymentTermsDays}d
                    </Td>
                    <Td right mono>
                      {summary.isPending ? "—" : fmt(cs?.committed ?? "0.00")}
                    </Td>
                    <Td right mono muted>
                      {summary.isPending ? "—" : fmt(cs?.billed ?? "0.00")}
                    </Td>
                    <Td right mono>
                      {summary.isPending ? "—" : fmt(cs?.outstanding ?? "0.00")}
                    </Td>
                    <Td right>
                      <div className="flex items-center justify-end gap-1">
                        {canManagePortalAccess(role) && (
                          <ChevronButton
                            open={isPortalOpen}
                            onClick={() => togglePortal(c.id)}
                            label="portal access"
                          />
                        )}
                        {owesThem && (
                          <Button variant="ghost" onClick={() => togglePaying(c.id)}>
                            {payingId === c.id ? "Cancel" : "Pay"}
                          </Button>
                        )}
                        <Button variant="ghost" onClick={() => onOpenContractor(c.id)}>
                          Open
                        </Button>
                        <Button variant="ghost" onClick={() => open(c)}>
                          Edit
                        </Button>
                        {role === "owner" && (
                          <DeleteButton
                            busy={del.deleting}
                            onConfirm={() => del.remove("contractor", c.id)}
                          />
                        )}
                      </div>
                    </Td>
                  </tr>
                  {isPortalOpen && canManagePortalAccess(role) && (
                    <tr>
                      <td colSpan={8} className="bg-neutral-50 px-4 py-4">
                        <ContractorPortalAccess slug={slug} contractorId={c.id} email={c.email} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </Table>

          {payingId && (
            <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-5">
              <h3 className="mb-3 text-sm font-medium">
                Bills owed to {r.query.data.contractors.find((c) => c.id === payingId)?.name}
              </h3>

              {payError && <ErrorNote>{payError}</ErrorNote>}

              {payingBills.isPending ? (
                <Loading />
              ) : owed.length === 0 ? (
                <Empty title="Nothing outstanding">
                  Every bill from this contractor is settled.
                </Empty>
              ) : (
                <Table
                  maxHeight={false}
                  head={
                    <>
                      <Th>Project</Th>
                      <Th>Ref</Th>
                      <Th right>Total</Th>
                      <Th right>Outstanding</Th>
                      <Th />
                    </>
                  }
                >
                  {owed.map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-neutral-100 last:border-0 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
                    >
                      <Td muted>{b.projectName}</Td>
                      <Td mono muted>
                        {b.number ?? "—"}
                      </Td>
                      <Td right mono>
                        {fmt(b.total)}
                      </Td>
                      <Td right mono>
                        {fmt(b.outstanding)}
                      </Td>
                      <Td right>
                        <Button
                          variant="ghost"
                          onClick={() => setSettlingBillId(settlingBillId === b.id ? null : b.id)}
                        >
                          {settlingBillId === b.id ? "Cancel" : "Record payment"}
                        </Button>
                      </Td>
                    </tr>
                  ))}
                  {owed
                    .filter((b) => b.id === settlingBillId)
                    .map((b) => (
                      <tr key={`${b.id}-form`}>
                        <td colSpan={5} className="bg-white px-4 py-4">
                          <RecordPaymentForm
                            settlement={b}
                            currency={b.currency}
                            noun="bill"
                            dateLabel="Paid on"
                            busy={pay.isPending}
                            onSubmit={(p) =>
                              pay.mutate(() =>
                                unwrap(
                                  api.api.c[":slug"].payables.payments.$post({
                                    param: { slug },
                                    json: {
                                      contractorId: b.contractorId,
                                      paidOn: p.on,
                                      amount: p.amount,
                                      currency: b.currency,
                                      ...(p.method ? { method: p.method } : {}),
                                      ...(p.reference ? { reference: p.reference } : {}),
                                      applications: [{ billId: b.id, amount: p.amount }],
                                    },
                                  }),
                                ),
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                </Table>
              )}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

/**
 * Who can sign in to view what this contractor is owed from outside the
 * company — the contractor-portal counterpart to `PortalAccess` in
 * Clients.tsx, scoped to one contractor instead of one client.
 *
 * Sends to the contractor's own address on file rather than a typed-in one,
 * same reasoning as the client side: the contact a portal login should reach
 * is whoever the company already has an email for.
 */
export function ContractorPortalAccess({
  slug,
  contractorId,
  email,
}: {
  slug: string;
  contractorId: string;
  email: string | null;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ email: string; link: string; mailed: boolean } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);

  const key = ["contractor-portal-invitations", slug, contractorId];
  const q = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].contractors[":id"]["portal-invitations"].$get({
          param: { slug, id: contractorId },
        }),
      ),
  });

  async function invite() {
    if (!email) return;
    setError(null);
    setBusy(true);
    try {
      const body = await unwrap(
        await api.api.c[":slug"].contractors[":id"]["portal-invitations"].$post({
          param: { slug, id: contractorId },
          json: { email },
        }),
      );
      setCopied(false);
      setIssued({
        email: body.invitation?.email ?? "",
        link: body.link,
        mailed: body.mailed === true,
      });
      qc.invalidateQueries({ queryKey: key });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(invId: string) {
    await unwrap(
      await api.api.c[":slug"].contractors[":id"]["portal-invitations"][":invId"].revoke.$post({
        param: { slug, id: contractorId, invId },
      }),
    );
    qc.invalidateQueries({ queryKey: key });
  }

  const pending = (q.data?.invitations ?? []).filter((i) => !i.acceptedAt);
  const active = (q.data?.portalUsers ?? []).filter((u) => !u.archivedAt);

  return (
    <div className="mb-4 border-b border-neutral-200 pb-4">
      <h3 className="mb-2 text-sm font-medium text-neutral-700">Portal access</h3>
      <p className="mb-3 max-w-prose text-xs text-neutral-500">
        Send this contractor a sign-in link to see what they're owed on their own — total, billed,
        paid, and still to come — and to submit draws for billing.
      </p>

      {email ? (
        <Button onClick={invite} disabled={busy}>
          {busy ? "Sending…" : `Send portal access to ${email}`}
        </Button>
      ) : (
        <p className="text-xs text-neutral-500">
          This contractor has no email on file — add one under "Edit" before sending portal access.
        </p>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      {issued && (
        <div className="mt-3 rounded-xl border border-accent-500/40 bg-accent-50/50 p-4 shadow-sm">
          <p className="text-sm">
            Invitation for <span className="font-medium">{issued.email}</span>.{" "}
            {issued.mailed
              ? "An email is on its way. Here's the link too, in case it doesn't arrive — it won't be shown again."
              : "Mail isn't configured, so send them this link yourself — it won't be shown again."}
          </p>
          <div className="mt-2 flex gap-2">
            <input className={input} readOnly value={issued.link} />
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(issued.link);
                setCopied(true);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      )}

      {(pending.length > 0 || active.length > 0) && (
        <div className="mt-3 space-y-1 text-sm">
          {active.map((u) => (
            <div key={u.id} className="flex items-center justify-between rounded-lg px-2 py-1">
              <span>
                {u.email} <span className="text-neutral-400">— active</span>
              </span>
            </div>
          ))}
          {pending.map((i) => (
            <div
              key={i.id}
              className="flex items-center justify-between rounded-lg px-2 py-1 hover:bg-white"
            >
              <span>
                {i.email} <span className="text-neutral-400">— invited, not yet accepted</span>
              </span>
              <Button variant="ghost" onClick={() => revoke(i.id)}>
                Revoke
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One form for both jobs. A contractor's details are the same fields whether
 * you are entering them for the first time or fixing a typo in them, and two
 * forms would be two places to add the next field to.
 */
function ContractorForm({
  initial,
  busy,
  formError,
  fieldErrors,
  onSubmit,
}: {
  initial: Contractor | null;
  busy: boolean;
  formError: string | null;
  fieldErrors: Record<string, string>;
  onSubmit: (body: ContractorBody) => void;
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [kind, setKind] = useState<"individual" | "company">(
    initial?.kind === "individual" ? "individual" : "company",
  );
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [taxId, setTaxId] = useState(initial?.taxId ?? "");
  const [rate, setRate] = useState(initial?.defaultRate ?? "");
  const [rateUnit, setRateUnit] = useState(initial?.rateUnit ?? "");
  const [terms, setTerms] = useState(String(initial?.paymentTermsDays ?? 15));
  const [notes, setNotes] = useState(initial?.notes ?? "");
  // Stored as a rate, typed as a percentage — see PercentInput.
  const [retainage, setRetainage] = useState(
    initial ? rateToPercent(initial.defaultRetainagePct) : "0",
  );

  return (
    <form
      className="mb-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name: name.trim(),
          kind,
          email: email.trim() || null,
          phone: phone.trim() || null,
          taxId: taxId.trim() || null,
          defaultRate: rate.trim() || null,
          // A rate with no unit is meaningless, so the unit follows the rate out.
          rateUnit: rate.trim() ? ((rateUnit || "hour") as "hour" | "day" | "fixed") : null,
          paymentTermsDays: Number(terms),
          defaultRetainagePct: rateFromPercent(retainage),
          notes: notes.trim() || null,
        });
      }}
    >
      {formError && <ErrorNote>{formError}</ErrorNote>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Name" error={fieldErrors.name}>
          <input
            className={input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </Field>
        <Field label="Type">
          <select
            className={input}
            value={kind}
            onChange={(e) => setKind(e.target.value as typeof kind)}
          >
            <option value="company">Company</option>
            <option value="individual">Individual</option>
          </select>
        </Field>
        {/* Typed as a percentage and stored as a rate. The conversion
            happens on submit, in one place, rather than every reader of
            this field having to remember which form it is in. */}
        <Field
          label="Retainage"
          hint="Held back from each bill"
          error={fieldErrors.defaultRetainagePct}
        >
          <PercentInput value={retainage} onChange={setRetainage} />
        </Field>
        <Field label="Email" error={fieldErrors.email}>
          <input
            className={input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Phone" error={fieldErrors.phone}>
          <input className={input} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Tax ID" error={fieldErrors.taxId}>
          <input className={input} value={taxId} onChange={(e) => setTaxId(e.target.value)} />
        </Field>
        <Field
          label="Default rate"
          hint="Leave blank if they quote per job"
          error={fieldErrors.defaultRate}
        >
          <input
            className={input}
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </Field>
        <Field label="Rate unit" error={fieldErrors.rateUnit}>
          <select
            className={input}
            value={rateUnit}
            disabled={!rate.trim()}
            onChange={(e) => setRateUnit(e.target.value)}
          >
            <option value="hour">Per hour</option>
            <option value="day">Per day</option>
            <option value="fixed">Fixed</option>
          </select>
        </Field>
        <Field label="Payment terms" hint="Days" error={fieldErrors.paymentTermsDays}>
          <input
            className={input}
            type="number"
            min={0}
            max={365}
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
          />
        </Field>
        <Field label="Notes" error={fieldErrors.notes}>
          <input className={input} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <div className="mt-3">
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : initial ? "Save changes" : "Add contractor"}
        </Button>
      </div>
    </form>
  );
}
